// src/components/access/UpsertUserModal.tsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { resendInviteService, getEmployees } from "@/services/adminService";
import { Send, RefreshCcw } from "lucide-react";
import { getErrorMessage } from "@/utils/errorHandler";
import ManagedEmployeesWarning, { isManagingAnyone } from "./ManagedEmployeesWarning";

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

const NO_EMPLOYEE = "none";

// How an employee record can be joined to this login:
//   { link: true }     — their placeholder login was never used; just link;
//   { merge: true }    — they use their own login: merge the two (existing
//                        admins only — a new one has nothing to merge);
//   { blocked: "why" } — not possible.
// The server makes the final call (e.g. a login that has been used before).
function linkOption(employee, editingUserId) {
  const login = employee?.user_id;
  if (!login || String(login._id) === String(editingUserId || "")) return { link: true };
  if (login.role === "admin") return { blocked: "already has admin access" };
  if (login.is_invitation_accepted) {
    return editingUserId ? { merge: true } : { blocked: "uses their own login" };
  }
  return { link: true };
}

export default function UpsertUserModal({
  open,
  onOpenChange,
  roles,
  editingUser,
  onSave,
  canGrantExisting = false,
}) {
  const isEdit = Boolean(editingUser?.email);
  // Inviting: someone new (an invite email and a new login), or an existing
  // employee — their own login gets admin access; no new account.
  const [source, setSource] = useState("new");
  const isExistingEmployee = !isEdit && source === "employee";
  const [grantEmployeeId, setGrantEmployeeId] = useState("");

  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [status, setStatus] = useState("invited");
  const [resending, setResending] = useState(false);
  // One login per person: the admin's own employee record, if they're also an
  // employee of the company.
  const [employeeId, setEmployeeId] = useState(NO_EMPLOYEE);
  const [employees, setEmployees] = useState([]);
  const linkedEmployee = editingUser?.linked_employee || null;
  // Merging with an employee who already uses their own login: which sign-in
  // (email + password) they keep.
  const [signInWith, setSignInWith] = useState("admin");
  const selectedEmployee = employees.find((emp) => String(emp._id) === employeeId) || null;
  const isMerge =
    Boolean(selectedEmployee) && linkOption(selectedEmployee, editingUser?._id).merge === true;
  // Deactivating someone who still manages employees asks for confirmation first.
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);

  const isDeactivatingManager =
    isEdit &&
    editingUser?.status === "active" &&
    status === "inactive" &&
    isManagingAnyone(editingUser);

  const roleOptions = useMemo(() => {
    return (Array.isArray(roles) ? roles : [])
      .filter((r) => !r?.is_default)
      .map((r) => ({
        id: String(r._id ?? r.id),
        name: r.name,
      }));
  }, [roles]);

  useEffect(() => {
    if (!open || linkedEmployee) return;
    let cancelled = false;
    getEmployees(undefined, undefined, { statuses: ["active", "exit_scheduled"] })
      .then((list) => {
        if (cancelled) return;
        const rows = Array.isArray(list) ? list : [];
        setEmployees(
          [...rows].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        );
      })
      .catch(() => !cancelled && setEmployees([]));
    return () => {
      cancelled = true;
    };
  }, [open, linkedEmployee]);

  useEffect(() => {
    if (!open) return;
    setConfirmingDeactivate(false);
    setEmployeeId(NO_EMPLOYEE);
    setSignInWith("admin");
    setSource("new");
    setGrantEmployeeId("");

    if (editingUser) {
      const selectedRole = (Array.isArray(roles) ? roles : []).find(
        (r) => String(r?._id ?? r?.id) === String(editingUser.access_role_id),
      );

      setName(editingUser.name || "");
      setEmail(editingUser.email || "");
      setRoleId(
        selectedRole && !selectedRole.is_default
          ? String(editingUser.access_role_id)
          : "",
      );
      setStatus(editingUser.status || "active");
    } else {
      setName("");
      setEmail("");
      setRoleId("");
      setStatus("invited");
    }
  }, [open, editingUser, roles]);

  // The Default Admin role isn't handed out or taken away here, but the user can
  // still be edited (status, name, their employee record) without picking a role.
  const isDefaultAdminUser =
    isEdit &&
    Boolean(
      (Array.isArray(roles) ? roles : []).find(
        (r) => String(r?._id ?? r?.id) === String(editingUser?.access_role_id),
      )?.is_default,
    );

  // Employees whose own login can be given admin access (not admins already).
  const grantableEmployees = employees.filter(
    (emp) => emp.user_id && emp.user_id.role !== "admin",
  );
  const grantEmployee = grantableEmployees.find((emp) => String(emp._id) === grantEmployeeId);

  const canSave = isExistingEmployee
    ? Boolean(grantEmployeeId) && Boolean(roleId)
    : isValidEmail(email) &&
      (Boolean(roleId) || isDefaultAdminUser) &&
      (!isEdit || Boolean(editingUser)); // small guard

  const handleClose = () => {
    if (saving) return;
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    try {
      if (!canSave) {
        toast.error(
          isExistingEmployee
            ? "Please pick an employee and a role."
            : "Please provide a valid email and select a role.",
        );
        return;
      }

      if (isExistingEmployee) {
        setSaving(true);
        await onSave({ grantAdminAccess: { employee_id: grantEmployeeId, roleId } });
        onOpenChange(false);
        return;
      }

      if (isDeactivatingManager && !confirmingDeactivate) {
        setConfirmingDeactivate(true);
        return;
      }

      setSaving(true);

      const payload = {
        email: email.trim(),
        name: name.trim() || undefined,
        roleId: roleId || undefined,
        status,
      };
      if (!linkedEmployee && employeeId !== NO_EMPLOYEE) {
        if (isMerge) {
          payload.merge = { employee_id: employeeId, sign_in_with: signInWith };
        } else {
          payload.employee_id = employeeId;
        }
      }

      // Include id for edit mode if present
      const id = editingUser?._id ?? editingUser?.id;
      if (isEdit && id) payload.id = String(id);

      await onSave(payload);
      onOpenChange(false);
    } catch (e) {
      const errorMessage = getErrorMessage(
        e,
        isEdit ? "Failed to update user" : "Failed to invite user",
      );
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleResendInvite = async () => {
    if (!editingUser?._id) return;

    try {
      setResending(true);
      await resendInviteService(editingUser._id);
      toast.success("Invitation resent successfully");
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to resend invitation"));
    } finally {
      setResending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {isEdit ? "Edit User" : isExistingEmployee ? "Give Admin Access" : "Invite User"}
          </DialogTitle>
        </DialogHeader>

        {confirmingDeactivate ? (
          <div className="space-y-3">
            <ManagedEmployeesWarning user={editingUser} action="deactivate" />
            <p className="text-sm text-muted-foreground">
              Deactivate anyway?
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {!isEdit && canGrantExisting && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Who is this for?</Label>
                <RadioGroup value={source} onValueChange={setSource} className="gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="new" id="source-new" />
                    <span>Someone new — send them an invite</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="employee" id="source-employee" />
                    <span>An existing employee — give their own login admin access</span>
                  </label>
                </RadioGroup>
              </div>
            )}

            {isExistingEmployee ? (
              <>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">
                    Employee <span className="text-red-500">*</span>
                  </Label>
                  <Select value={grantEmployeeId} onValueChange={setGrantEmployeeId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select an employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {grantableEmployees.map((emp) => (
                        <SelectItem key={emp._id} value={String(emp._id)}>
                          {emp.name || "Unnamed"} ({emp.employee_id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {grantEmployee && (grantEmployee.email || grantEmployee.user_id?.email) && (
                    <p className="text-xs text-muted-foreground">
                      Signs in with {grantEmployee.email || grantEmployee.user_id?.email}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">
                    Role <span className="text-red-500">*</span>
                  </Label>
                  <Select value={roleId} onValueChange={setRoleId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <p className="text-xs text-muted-foreground">
                  They keep their own login and email — no invite is sent. From
                  their next sign-in they also see the admin sections this role
                  allows, alongside their own employee pages.
                </p>
                {grantEmployee && !grantEmployee.user_id?.is_invitation_accepted && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    They haven't activated their employee portal login yet. They
                    can sign in once they accept their portal invitation.
                  </p>
                )}
              </>
            ) : (
            <>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Rahul Sharma"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g., user@company.com"
              />
              {!isValidEmail(email) && email.trim().length > 0 && (
                <p className="text-xs text-red-500">Invalid email format</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                Role <span className="text-red-500">*</span>
              </Label>

              {isDefaultAdminUser ? (
                <p className="text-sm text-foreground">
                  Default Admin
                  <span className="block text-xs text-muted-foreground">
                    This role can't be changed here.
                  </span>
                </p>
              ) : (
                <Select value={roleId} onValueChange={setRoleId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>

                  <SelectContent>
                    {roleOptions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Status</Label>

              <Select
                value={status}
                onValueChange={(v) => setStatus(v)}
                disabled={!isEdit || editingUser?.status === "invited" || isDefaultAdminUser}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>

                <SelectContent>
                  {status === "invited" ? (
                    <SelectItem value="invited" disabled>
                      Invited
                    </SelectItem>
                  ) : (
                    <>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>

              {editingUser?.status === "invited" && (
                <p className="text-xs text-muted-foreground">
                  Invited users cannot be activated until they accept the
                  invitation.
                </p>
              )}
              {linkedEmployee && status === "inactive" && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  This is also their employee login — deactivating it blocks
                  their employee workspace too. To take away only admin access,
                  delete the user instead.
                </p>
              )}
            </div>

            {isEdit && (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Employee record</Label>
              {linkedEmployee ? (
                <p className="text-sm text-foreground">
                  Linked to employee{" "}
                  <span className="font-medium">{linkedEmployee.employee_id}</span>.
                  <span className="block text-xs text-muted-foreground">
                    They use this one login for their own workspace and the
                    admin sections their role allows.
                  </span>
                </p>
              ) : (
                <>
                  <Select value={employeeId} onValueChange={setEmployeeId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Not an employee" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_EMPLOYEE}>Not an employee</SelectItem>
                      {employees.map((emp) => {
                        const option = linkOption(emp, editingUser?._id);
                        return (
                          <SelectItem
                            key={emp._id}
                            value={String(emp._id)}
                            disabled={Boolean(option.blocked)}
                          >
                            {emp.name || "Unnamed"} ({emp.employee_id})
                            {option.blocked ? ` — ${option.blocked}` : ""}
                            {option.merge ? " — has own login, merge" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {isMerge && (
                    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                      <p>
                        {selectedEmployee.name || "This employee"} already signs in
                        with their own login. Merging makes one login: it keeps this
                        admin access and takes over their employee record and history.
                        The other login is removed.
                      </p>
                      <p className="font-medium">Keep signing in with:</p>
                      <RadioGroup value={signInWith} onValueChange={setSignInWith}>
                        <label className="flex items-center gap-2">
                          <RadioGroupItem value="admin" id="merge-admin" />
                          <span>{editingUser?.email} (this admin login)</span>
                        </label>
                        <label
                          className={`flex items-center gap-2 ${isDefaultAdminUser ? "opacity-60" : ""}`}
                        >
                          <RadioGroupItem
                            value="employee"
                            id="merge-employee"
                            disabled={isDefaultAdminUser}
                          />
                          <span>
                            {selectedEmployee.email || selectedEmployee.user_id?.email} (their
                            employee login)
                          </span>
                        </label>
                      </RadioGroup>
                      <p className="text-xs">
                        {isDefaultAdminUser
                          ? "The Default Admin always keeps their own email and password."
                          : "They keep that login's password. The other email stops working."}
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    If this person also works here as an employee, pick their
                    record: from their next sign-in they'll use one login for
                    their own workspace and the admin sections their role
                    allows. The employee record
                    then shows this login's name and email, and the link can't
                    be undone. If they already use their own employee login,
                    picking them merges the two logins.
                  </p>
                </>
              )}
            </div>
            )}
            </>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 justify-between">
          {/* Left Side – Resend Invite */}
          {isEdit && editingUser?.status === "invited" && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleResendInvite}
              disabled={resending}
              className="gap-2"
            >
              {resending ? (
                 <RefreshCcw
                    size={14}
                    strokeWidth={2.5}
                    className="animate-spin"
                  />
              ) : (
                <Send size={14} />
              )}
              {resending ? "Sending..." : "Resend Invitation"}
            </Button>
          )}

          {/* Right Side – Main Actions */}
          <div className="flex gap-2 ml-auto">
            {confirmingDeactivate ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setConfirmingDeactivate(false)}
                  disabled={saving}
                >
                  Back
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Deactivate anyway"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleClose} disabled={saving}>
                  Cancel
                </Button>

                <Button onClick={handleSubmit} disabled={!canSave || saving}>
                  {saving
                    ? "Saving..."
                    : isExistingEmployee
                      ? "Give admin access"
                      : isMerge
                        ? "Merge & Save"
                        : isEdit
                          ? "Save Changes"
                          : "Send Invite"}
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
